import DailyDealGif from '@imageTheme/mobile/CyberMonday2024/CM2024-DailyDeals-ProductPage-Gif.gif';
import Image from 'next/image';
import {useRef} from 'react';
import {CustomerReviewsForProductWithLink, remapFord09SkuTo15Sku} from '@components/widgets/Common';
import {FrequentlyBoughtTogether} from '@components/product/global/FrequentlyBoughtTogether';
import {VariantSelector} from '@components/product/ProductPageTemplates/Components/VariantSelector';
import {SingleProductFocusPricing} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusPricing';
import {SingleProductFocusBreadcrumbs} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusBreadcrumbs';
import {SingleProductFocusGallery} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusGallery';
import {SingleProductFocusAddToCart} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusAddToCart';
import {SingleProductFocusBuildProductTabs} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusBuildTabs';
import {SingleProductFocusMultipleVariantSelectors} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusMultipleVariantSelectors';
import {ProductPageIconsForFreeShippingMoneyBackAndWarranty} from '@components/product/global/ProductPageIconsForFreeShippingMoneyBackAndWarranty';
import {SingleProductFocusInStockIndicator} from '@components/product/ProductPageTemplates/Components/SingleProductFocusWithAssets/SingleProductFocusInStockIndicator';

export const SingleProductFocusWithAssetsProductDetails = ({
  product,
  variants,
  selectedVariant,
  reviewsFromDb,
  frequentProducts,
  selectedOptions,
  setSelectedOptions,
  alternativeVariantSelector
}) => {
  // console.log("[SingleProductFocusWithAssetsProductDetails] product: ", product);
  const sku = remapFord09SkuTo15Sku(selectedVariant);
  const productTitle = selectedVariant?.siteName ?? selectedVariant?.productTitle;

  const breadCrumbLevel1Path = product?.breadCrumbLevel1Path;
  const breadCrumbLevel1Title = product?.breadCrumbLevel1Title;

  const breadCrumbLevel2Path = product?.breadCrumbLevel2Path;
  const breadCrumbLevel2Title = product?.breadCrumbLevel2Title;

  const siteCollection = product?.siteCollection;

  const tabMedia = product?.tabMedia;
  const tabDescription = product?.description;

  const parentQuickFactsTable = product?.quickFactsTable;
  const selectedQuickFactsTable =
    selectedVariant?.quickFactsTable ?? selectedVariant?.quickFactsTable;

  const quickFactsTable = {...selectedQuickFactsTable, ...parentQuickFactsTable};

  const handleScroll = () => {
    const reviewsAnchor = document.getElementById('product-reviews');
    reviewsAnchor.scrollIntoView({behavior: 'smooth'});
  };

  const reviewsAnchor = useRef(undefined);

  const isDailyDeal = product?.category === 'custom-bundle';
  // console.log(product);

  const primaryVariantSelector = (
    <SingleProductFocusProductPriceAndAddToCart
      selectedVariant={selectedVariant}
      product={product}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      isDailyDeal={isDailyDeal}
    />
  );

  return (
    <div className="mt-0 lg:mt-8 lg:mb-16">
      <div className={'block lg:hidden px-2 mb-2 text-sm pt-2'}>
        <SingleProductFocusBreadcrumbs
          categoryLink={breadCrumbLevel1Path}
          categoryName={breadCrumbLevel1Title}
          collectionLink={breadCrumbLevel2Path}
          collectionName={breadCrumbLevel2Title}
          productName={sku}
        />
      </div>

      <div className="md:grid grid-cols-12 gap-x-8">
        <div className={'col-span-7'}>
          <SingleProductFocusGallery variants={variants} product={selectedVariant} />
        </div>

        <div className={'col-span-5'}>
          <div className={'hidden lg:block'}>
            <SingleProductFocusBreadcrumbs
              categoryLink={breadCrumbLevel1Path}
              categoryName={breadCrumbLevel1Title}
              collectionLink={breadCrumbLevel2Path}
              collectionName={breadCrumbLevel2Title}
              productName={sku}
            />
          </div>

          <div className={'px-2 lg:px-0 space-y-2'}>
            <ProductTitleDesktop productTitle={productTitle} isDailyDeal={isDailyDeal} />

            {reviewsFromDb?.count > 0 ? (
              <CustomerReviewsForProductWithLink
                product={product}
                reviewsAnchor={reviewsAnchor}
                reviewsFromDb={reviewsFromDb}
                handleScrollToReviews={handleScroll}
              />
            ) : null}

            {alternativeVariantSelector ? (
              <SingleProductFocusMultipleVariantSelectors
                alternativeVariantSelector={alternativeVariantSelector}
                primaryVariantSelector={primaryVariantSelector}
              />
            ) : (
              primaryVariantSelector
            )}
          </div>

          {isDailyDeal ? (
            <div className={'mt-4 mb-2'}>
              <ProductPageIconsForFreeShippingMoneyBackAndWarranty />
            </div>
          ) : null}

          <div className={'mt-6 mb-4'}>
            <SingleProductFocusBuildProductTabs
              product={selectedVariant}
              variants={variants}
              tabMedia={tabMedia}
              tabDescription={tabDescription}
              tabDescriptionTable={quickFactsTable}
              siteCollection={siteCollection}
            />
          </div>

          {frequentProducts?.length > 0 && <FrequentlyBoughtTogether products={frequentProducts} />}
        </div>
      </div>
    </div>
  );
};

const ProductTitleDesktop = ({productTitle, isDailyDeal}) => {
  return (
    <div className={'mt-1 mb-1 lg:mt-4'}>
      {isDailyDeal && (
        <div className={'w-full lg:max-w-[300px] mb-1 rounded-lg overflow-hidden '}>
          <Image src={DailyDealGif} alt={'Daily Deal'} width={600} height={100} />
        </div>
      )}
      <h1 className="text-xl lg:text-2xl text-black font-bold" tabIndex="0">
        {productTitle}
      </h1>
    </div>
  );
};

export const SingleProductFocusProductPriceAndAddToCart = ({
  product,
  selectedVariant,
  selectedOptions,
  setSelectedOptions,
  isDailyDeal
}) => {
  return (
    <div className={'space-y-4'}>
      {selectedOptions ? (
        <VariantSelector
          selectedVariant={selectedVariant}
          parentVariant={product}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
        />
      ) : null}

      <div className={'flex flex-col flex-wrap'}>
        <SingleProductFocusPricing product={selectedVariant} isDailyDeal={isDailyDeal} />

        <SingleProductFocusInStockIndicator product={selectedVariant} />
      </div>

      <SingleProductFocusAddToCart product={selectedVariant} />
    </div>
  );
};

const AffirmPrice = () => {
  return <div className="affirm-price" style={{}}></div>;
};

const DiscontinuedCenter = ({isDiscontinued, children}) => {
  const isDiscontinuedCenterClass = isDiscontinued
    ? 'flex justify-center items-center text-center'
    : '';

  return (
    <div
      className={isDiscontinuedCenterClass}
      style={
        isDiscontinued
          ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }
          : {}
      }>
      {children}
    </div>
  );
};
